"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CommissionEventSchema = exports.COMMISSION_EVENT_STATUSES = exports.COMMISSION_SOURCE_TYPES = void 0;
exports.commissionEventId = commissionEventId;
exports.reversalEventId = reversalEventId;
exports.buildReversal = buildReversal;
exports.summariseCommissionEvents = summariseCommissionEvents;
exports.resolvePeriod = resolvePeriod;
const zod_1 = require("zod");
/**
 * COMMISSION EVENTS — the ledger. One immutable row per thing a partner earned.
 *
 * ---------------------------------------------------------------------------
 * WHY A LEDGER AND NOT A COUNTER
 * ---------------------------------------------------------------------------
 *
 * Before this, a partner's earnings were ONE mutable number computed once at
 * the first subscription conversion and stamped on the referral row. That
 * number cannot be audited, disputed, explained or clawed back, and it silently
 * disagreed with the payout query that derived from rows. Every question a
 * partner or an accountant actually asks — "what did I earn in October", "why
 * was I paid this", "what happened to the commission on that refunded
 * terminal" — needs a row per earning, not a running total.
 *
 * ---------------------------------------------------------------------------
 * THE RULES THAT MAKE IT TRUSTWORTHY
 * ---------------------------------------------------------------------------
 *
 * 1. IMMUTABLE. A row is never edited after it is written. A reversal is a NEW
 *    row with a negative `commissionCents` pointing at the original via
 *    `reversesEventId` — so the history shows what happened, not a number that
 *    quietly changed.
 * 2. SELF-EXPLAINING. Each row carries `planId`, `planVersion` and `ruleId` —
 *    the receipt. Rates change; a payout made under v3 must still explain
 *    itself after the plan reaches v7, which a pointer to "the current plan"
 *    could never do.
 * 3. IDEMPOTENT. `eventId` is DERIVED from the thing that caused it, so a
 *    retried webhook overwrites its own row instead of paying twice. Nothing
 *    here may use a random id.
 * 4. CLEARING IS A DATE, NOT A JOB. `clearingDate` is stamped at write time;
 *    whether money has cleared is decided by comparing it to now (see
 *    partnerBalance.ts). A late or failed cron can never cost a partner money.
 */
exports.COMMISSION_SOURCE_TYPES = [
    'device_sale',
    'package_addon',
    'subscription_payment',
    'adjustment',
];
/**
 * `pending`   — earned, clearing window not yet stamped (rare; a defect state).
 * `clearing`  — earned, inside its clearing window.
 * `cleared`   — through the window, payable.
 * `paid`      — included in a settled payout.
 * `forfeited` — reversed: churn, refund, chargeback.
 */
exports.COMMISSION_EVENT_STATUSES = ['pending', 'clearing', 'cleared', 'paid', 'forfeited'];
exports.CommissionEventSchema = zod_1.z.object({
    /** DERIVED, never random — see `commissionEventId`. Partition-unique. */
    eventId: zod_1.z.string().min(1),
    partnerId: zod_1.z.string().min(1),
    tenantId: zod_1.z.string().optional(),
    /** Epoch ms the earning happened (the invoice/sale), NOT when the row was written. */
    occurredAt: zod_1.z.number(),
    sourceType: zod_1.z.enum(exports.COMMISSION_SOURCE_TYPES),
    /** Invoice id / order id / sku id — whatever identifies the causing thing. */
    sourceRef: zod_1.z.string().min(1),
    /** Human label for the portal: "2 × TC-Touch K10", "Compliance & Age Verify". */
    sourceLabel: zod_1.z.string().optional(),
    /** What the customer paid for this line, integer cents. */
    grossCents: zod_1.z.number().int(),
    /** Our cost, when the rule needed one (margin basis). Absent otherwise. */
    costCents: zod_1.z.number().int().optional(),
    // ---- the receipt: how this number was arrived at ----
    planId: zod_1.z.string().min(1),
    planVersion: zod_1.z.number().int().positive(),
    ruleId: zod_1.z.string().min(1),
    basisApplied: zod_1.z.string().min(1),
    rateApplied: zod_1.z.number(),
    /** The money. NEGATIVE on a reversal row. Integer cents. */
    commissionCents: zod_1.z.number().int(),
    status: zod_1.z.enum(exports.COMMISSION_EVENT_STATUSES),
    /** Epoch ms this becomes withdrawable. Absent = never auto-clears. */
    clearingDate: zod_1.z.number().optional(),
    clearedAt: zod_1.z.number().optional(),
    paidAt: zod_1.z.number().optional(),
    payoutId: zod_1.z.string().optional(),
    /** Set on a reversal row: the eventId being undone. */
    reversesEventId: zod_1.z.string().optional(),
    /** Why, when reversed or adjusted. Shown to the partner verbatim. */
    reason: zod_1.z.string().optional(),
    /** Which deal and which shared link produced this, when known. */
    dealId: zod_1.z.string().optional(),
    linkId: zod_1.z.string().optional(),
    /**
     * THE DEVICE ORDER this earning came from, when it came from one
     * (2026-09-03).
     *
     * `sourceRef` cannot answer this. For hardware sold at signup it is
     * `onboarding-hw:<tenantId>` — it identifies the CHECKOUT, not the order
     * row, so nothing could join a partner's earnings back to the order an
     * operator is looking at. That made the order screen unable to say who
     * earned what on it, which is the whole point of showing a commission
     * there.
     *
     * ADDITIVE and OPTIONAL, deliberately: `sourceRef` is part of the
     * idempotency key (`commissionEventId`), so widening THAT would re-key every
     * existing row and let a replay pay twice. This field is carried alongside
     * it and never participates in the key. Absent on every row written before
     * this date, and on every earning that is not a device sale — readers must
     * treat absence as "not known", never as "no order".
     */
    orderId: zod_1.z.string().optional(),
    /** For a `monthly` rule: which cycle this is, 0-based. Absent for one-offs. */
    cycleIndex: zod_1.z.number().int().min(0).optional(),
    createdAt: zod_1.z.number(),
});
/**
 * The idempotency key. DERIVED from what caused the earning, never random.
 *
 * Stripe redelivers webhooks; a retry must overwrite its own row rather than
 * pay a partner twice. `cycleIndex` is part of the key so a monthly rule's
 * twelve payments are twelve distinct rows rather than one row overwritten
 * eleven times.
 *
 * `ruleId` is included because one invoice line can legitimately produce
 * separate earnings under different rules if a plan ever splits them; without
 * it the second would silently overwrite the first.
 */
function commissionEventId(parts) {
    const cycle = typeof parts.cycleIndex === 'number' ? `#${parts.cycleIndex}` : '';
    return `cev_${parts.partnerId}_${parts.sourceType}_${parts.sourceRef}_${parts.ruleId}${cycle}`;
}
/** The id a reversal row uses, so a reversal is itself idempotent. */
function reversalEventId(originalEventId) {
    return `${originalEventId}_rev`;
}
/**
 * Build the reversal of an event. The original is NOT modified — the ledger
 * shows both, and every total is the sum of the two.
 */
function buildReversal(original, reason, now) {
    return {
        ...original,
        eventId: reversalEventId(original.eventId),
        commissionCents: -original.commissionCents,
        status: 'forfeited',
        reversesEventId: original.eventId,
        reason,
        occurredAt: now,
        createdAt: now,
        // A reversal is never "clearing" — it takes effect immediately.
        clearingDate: undefined,
        clearedAt: undefined,
        paidAt: undefined,
        payoutId: undefined,
    };
}
/**
 * Totals for a date range, over the rows given.
 *
 * `from` is INCLUSIVE and `to` is EXCLUSIVE — the half-open convention, so
 * consecutive months tile exactly with no row counted twice at a boundary and
 * none dropped. A caller asking for "October" passes 1 Oct 00:00 and 1 Nov
 * 00:00.
 *
 * Filters on `occurredAt` (when the money was earned), never `createdAt` (when
 * we happened to write the row) — a late webhook must land in the month it was
 * earned, or two reports of the same month disagree.
 */
function summariseCommissionEvents(events, fromMs, toMs) {
    const summary = {
        count: 0, totalCents: 0, bySource: {}, byStatus: {}, tenantCount: 0,
    };
    const tenants = new Set();
    for (const e of events ?? []) {
        if (!e || typeof e.occurredAt !== 'number')
            continue;
        if (e.occurredAt < fromMs || e.occurredAt >= toMs)
            continue;
        const cents = Number.isFinite(e.commissionCents) ? Math.trunc(e.commissionCents) : 0;
        summary.count += 1;
        summary.totalCents += cents;
        const src = summary.bySource[e.sourceType] || { count: 0, totalCents: 0 };
        src.count += 1;
        src.totalCents += cents;
        summary.bySource[e.sourceType] = src;
        const st = summary.byStatus[e.status] || { count: 0, totalCents: 0 };
        st.count += 1;
        st.totalCents += cents;
        summary.byStatus[e.status] = st;
        if (e.tenantId)
            tenants.add(e.tenantId);
    }
    summary.tenantCount = tenants.size;
    return summary;
}
function resolvePeriod(key, nowMs) {
    const d = new Date(nowMs);
    const y = d.getFullYear();
    const m = d.getMonth();
    switch (key) {
        case 'this_month':
            return { fromMs: new Date(y, m, 1).getTime(), toMs: new Date(y, m + 1, 1).getTime() };
        case 'last_month':
            return { fromMs: new Date(y, m - 1, 1).getTime(), toMs: new Date(y, m, 1).getTime() };
        case 'this_quarter': {
            const qStart = Math.floor(m / 3) * 3;
            return { fromMs: new Date(y, qStart, 1).getTime(), toMs: new Date(y, qStart + 3, 1).getTime() };
        }
        case 'year_to_date':
        default:
            // `to` is the START OF TOMORROW so today's earnings are included — a YTD
            // report that stops at midnight this morning looks like money vanished.
            return {
                fromMs: new Date(y, 0, 1).getTime(),
                toMs: new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1).getTime(),
            };
    }
}
//# sourceMappingURL=commissionEvent.js.map